<template>
  <div class="org-page">
    <div class="filter-card">
      <div class="filter-card__body">
        <div class="filter-card__heading">
          <span class="filter-card__title">{{ $t('org.my.title') }}</span>
          <span class="filter-card__sub">{{ $t('org.my.tip') }}</span>
        </div>
        <span class="filter-card__actions">
          <a-button type="primary" icon="plus" :disabled="hasTopOrg" @click="openCreate">{{ $t('org.create.button') }}</a-button>
          <a-button icon="team" @click="openJoin">{{ $t('org.join.button') }}</a-button>
          <a-button icon="reload" @click="loadOrgs">{{ $t('org.refresh') }}</a-button>
        </span>
      </div>
    </div>

    <div class="table-card">
      <a-table
        :columns="columns"
        :data-source="orgs"
        :loading="loading"
        :pagination="false"
        :locale="locale"
        row-key="id"
        :key="tableKey"
        :indent-size="40"
        default-expand-all-rows>
        <span slot="invite_code" slot-scope="text, record">
          <span class="org-code">{{ record.invite_code }}</span>
          <a class="org-copy" @click="copyInviteCode(record)">{{ $t('org.copy') }}</a>
        </span>
        <div slot="action" slot-scope="text, record">
          <a-button type="link" icon="apartment" @click="openCreateSub(record)">{{ $t('org.create.sub.button') }}</a-button>
          <a-button v-if="record.is_owner" type="link" icon="sync" @click="changeInviteCode(record)">{{ $t('org.invite.change.button') }}</a-button>
          <a-button v-if="record.is_owner" type="link" icon="delete" class="org-delete" @click="openDelete(record)">{{ $t('org.delete.button') }}</a-button>
        </div>
      </a-table>
    </div>

    <!-- 删除组织: 输入组织名称确认 -->
    <a-modal
      v-model="deleteVisible"
      :title="$t('org.delete.modal.title')"
      :confirm-loading="deleteLoading"
      :mask-closable="false"
      :ok-button-props="{ disabled: !deleteConfirmOk }"
      @ok="handleDelete">
      <p class="org-delete-tip">
        {{ $t('org.delete.confirm.prefix') }}
        <span class="org-delete-name">{{ deleteOrg ? deleteOrg.name : '' }}</span>
        {{ $t('org.delete.confirm.suffix') }}
      </p>
      <a-input
        v-model="deleteName"
        size="large"
        :placeholder="$t('org.delete.confirm.placeholder')"
        allow-clear
        @pressEnter="handleDelete" />
    </a-modal>

    <!-- 创建组织 / 创建下级组织 -->
    <a-modal
      v-model="createVisible"
      :title="createParentOrg ? $t('org.create.sub.modal.title').replace('{name}', createParentOrg.name) : $t('org.create.modal.title')"
      :confirm-loading="createLoading"
      :mask-closable="false"
      @ok="handleCreate">
      <a-input
        v-model="createName"
        size="large"
        :placeholder="createParentId ? $t('org.create.sub.placeholder') : $t('org.create.placeholder')"
        allow-clear
        @pressEnter="handleCreate" />
    </a-modal>

    <!-- 加入组织 -->
    <a-modal
      v-model="joinVisible"
      :title="$t('org.join.modal.title')"
      :confirm-loading="joinLoading"
      :mask-closable="false"
      @ok="handleJoin">
      <a-input
        v-model="inviteCode"
        size="large"
        :placeholder="$t('org.join.placeholder')"
        allow-clear
        @pressEnter="handleJoin" />
      <p v-if="joinOrgLoading" class="org-join-preview org-join-preview--muted">{{ $t('org.join.searching') }}</p>
      <p v-else-if="joinOrgPreview" class="org-join-preview">{{ $t('org.join.org.name').replace('{name}', joinOrgPreview.name) }}</p>
      <p v-else-if="inviteCode && inviteCode.trim()" class="org-join-preview org-join-preview--error">{{ $t('org.join.not.found') }}</p>
    </a-modal>
  </div>
</template>

<script>
import { changeOrganizationInviteCode, createOrganization, deleteOrganization, joinOrganization, loadMyOrganization, loadOrganizationByInviteCode } from '@/api/data'
import { getI18nLanguage } from '@/locales'

export default {
  name: 'Organization',
  data () {
    return {
      loading: false,
      orgs: [],
      tableKey: 0,
      hasTopOrg: false,
      createVisible: false,
      createLoading: false,
      createName: '',
      createParentId: null,
      createParentOrg: null,
      joinVisible: false,
      joinLoading: false,
      inviteCode: '',
      joinOrgPreview: null,
      joinOrgLoading: false,
      lookupTimer: null,
      deleteVisible: false,
      deleteLoading: false,
      deleteOrg: null,
      deleteName: '',
      locale: {
        emptyText: this.$t('org.empty.tip')
      },
      columns: [{
        title: this.$t('org.name'),
        dataIndex: 'name',
        key: 'name',
        align: 'left',
        width: 240
      }, {
        title: this.$t('org.admin.count'),
        dataIndex: 'admin_count',
        key: 'admin_count',
        width:100,
        align: 'center'
      }, {
        title: this.$t('org.driver.count'),
        dataIndex: 'driver_count',
        key: 'driver_count',
         width:100,
        align: 'center'
      }, {
        title: this.$t('org.invite.code'),
        dataIndex: 'invite_code',
        key: 'invite_code',
        align: 'center',
          width:160,
        scopedSlots: { customRender: 'invite_code' }
      }, {
        title: this.$t('org.create.time'),
        dataIndex: 'create_time',
        key: 'create_time',
        align: 'center'
      }, {
        title: this.$t('org.action'),
        dataIndex: 'action',
        key: 'action',
        align: 'center',
        scopedSlots: { customRender: 'action' }
      }]
    }
  },
  watch: {
    inviteCode (val) {
      this.lookupInviteOrg(val)
    }
  },
  computed: {
    deleteConfirmOk () {
      return !!(this.deleteName && this.deleteOrg && this.deleteName.trim() === this.deleteOrg.name)
    }
  },
  created () {
    this.loadOrgs()
  },
  methods: {
    getErrorMessage (msg) {
      if (typeof msg === 'string') {
        return msg
      }
      const lang = getI18nLanguage()
      const langKey = lang.replace('-', '').toLowerCase()
      if (langKey.includes('zh')) {
        return msg['zh'] || msg['zh-CN'] || msg['en'] || msg['en-US'] || ''
      } else if (langKey.includes('ja')) {
        return msg['ja'] || msg['ja-JP'] || msg['en'] || msg['en-US'] || ''
      }
      return msg['en'] || msg['en-US'] || ''
    },
    loadOrgs () {
      this.loading = true
      loadMyOrganization({}).then(res => {
        if (res.code === 0) {
          const list = (res.result && res.result.orgs) || []
          this.hasTopOrg = list.some(o => Number(o.parent_id) === 0)
          this.orgs = this.buildTree(list)
          this.tableKey++
        }
      }).finally(() => {
        this.loading = false
      })
    },
    buildTree (orgs) {
      const map = {}
      orgs.forEach(o => {
        o.children = []
        map[o.id] = o
      })
      const roots = []
      orgs.forEach(o => {
        if (o.parent_id && map[o.parent_id]) {
          map[o.parent_id].children.push(o)
        } else {
          roots.push(o)
        }
      })
      return roots
    },
    openCreate () {
      this.createName = ''
      this.createParentId = null
      this.createParentOrg = null
      this.createVisible = true
    },
    openCreateSub (record) {
      this.createName = ''
      this.createParentId = record.id
      this.createParentOrg = record
      this.createVisible = true
    },
    handleCreate () {
      const name = (this.createName || '').trim()
      if (!name) {
        this.$message.warning(this.$t('org.create.name.required'))
        return
      }
      this.createLoading = true
      createOrganization({ name, parent_id: this.createParentId }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.create.success'))
          this.createVisible = false
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.create.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.create.fail'))
      }).finally(() => {
        this.createLoading = false
      })
    },
    openJoin () {
      this.inviteCode = ''
      this.joinVisible = true
    },
    lookupInviteOrg (val) {
      clearTimeout(this.lookupTimer)
      const code = (val || '').trim()
      this.joinOrgPreview = null
      this.joinOrgLoading = false
      if (!code) {
        return
      }
      this.joinOrgLoading = true
      this.lookupTimer = setTimeout(() => {
        loadOrganizationByInviteCode({ invite_code: code }).then(res => {
          this.joinOrgLoading = false
          if (res.code === 0 && res.result && res.result.org) {
            this.joinOrgPreview = res.result.org
          } else {
            this.joinOrgPreview = null
          }
        }).catch(() => {
          this.joinOrgLoading = false
          this.joinOrgPreview = null
        })
      }, 400)
    },
    handleJoin () {
      const code = (this.inviteCode || '').trim()
      if (!code) {
        this.$message.warning(this.$t('org.join.code.required'))
        return
      }
      this.joinLoading = true
      joinOrganization({ invite_code: code }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.join.success'))
          this.joinVisible = false
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.join.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.join.fail'))
      }).finally(() => {
        this.joinLoading = false
      })
    },
    copyText (text, successKey, failKey) {
      const onSuccess = () => this.$message.success(this.$t(successKey))
      const onFail = () => this.$message.warning(this.$t(failKey))
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(onSuccess).catch(onFail)
      } else {
        onFail()
      }
    },
    copyInviteCode (record) {
      if (!record || !record.invite_code) {
        return
      }
      this.copyText(record.invite_code, 'org.copy.success', 'org.copy.fail')
    },
    changeInviteCode (record) {
      changeOrganizationInviteCode({ org_id: record.id }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.invite.change.success'))
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.invite.change.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.invite.change.fail'))
      })
    },
    openDelete (record) {
      this.deleteOrg = record
      this.deleteName = ''
      this.deleteVisible = true
    },
    handleDelete () {
      if (!this.deleteConfirmOk) {
        this.$message.warning(this.$t('org.delete.name.not.match'))
        return
      }
      this.deleteLoading = true
      deleteOrganization({ org_id: this.deleteOrg.id }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.delete.success'))
          this.deleteVisible = false
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.delete.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.delete.fail'))
      }).finally(() => {
        this.deleteLoading = false
      })
    }
  }
}
</script>

<style scoped lang="less">
@primary: #52C41A;
@radius: 6px;
@border-color: #e8e8e8;
@text-primary: rgba(0, 0, 0, 0.85);
@shadow-card: 0 1px 2px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);

.org-page {
  margin: 0 auto;
}

.filter-card {
  background: #fff;
  border-radius: @radius;
  box-shadow: @shadow-card;
  padding: 16px 24px;
  margin-bottom: 16px;

  &__body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: @text-primary;
  }

  &__heading {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__sub {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}

.table-card {
  background: #fff;
  border-radius: @radius;
  box-shadow: @shadow-card;
  padding: 12px 16px;
}

.org-code {
  font-family: Consolas, Monaco, monospace;
  letter-spacing: 1px;
  margin-right: 10px;
}

.org-copy {
  color: @primary;
}

.org-delete {
  color: #ff4d4f;
}

.org-delete-tip {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 16px;
}

.org-delete-name {
  color: #ff4d4f;
  font-weight: 600;
}

.org-join-preview {
  font-size: 13px;
  margin-top: 12px;
  color: @primary;

  &--muted {
    color: rgba(0, 0, 0, 0.45);
  }

  &--error {
    color: #ff4d4f;
  }
}
</style>
